import React, { ComponentType, MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import { IconSize, IconWrapProps } from '@wix/da-ds/pkg/Icons/IconWrap';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import { BiData, SubmitDeleteMultiImageFileClickBiEvent } from '@wix/da-bi/pkg/events';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { PapiAttachmentFile, PapiDeviation } from '@wix/da-papi-types';
import CloseIcon from '@wix/da-ds/pkg/Icons/24x24/Close';
import { toMediaSrcFile } from '@wix/da-shared-react/pkg/utils/DeviationMediaService/helpers';
import PreviewThumb, { Props as PreviewThumbProps } from '../../PreviewThumb';

import s from './ImageAttachmentThumb.scss';

interface Props extends PreviewThumbProps {
  deviation?: PapiDeviation;
  attachment: PapiAttachmentFile;
  isRemoved: boolean;
  selected: boolean;
  overlayIcon?: ComponentType<IconWrapProps>;
  onRemove: MouseEventHandler<HTMLButtonElement> & MouseEventHandler<HTMLAnchorElement>;
}

const ImageAttachmentThumb: React.FC<Props> = ({
  deviation,
  attachment,
  isRemoved,
  selected,
  onRemove,
  className,
  overlayIcon: OverlayIcon,
  ...props
}) => {
  const { t } = useTranslation();
  const selectedMediaType = attachment.media!.types![0];
  const deviationAttachmentIds = (deviation?.extended?.additionalMedia || []).map(
    att => att.fileId
  );
  const thumbData = toMediaSrcFile(attachment, selectedMediaType);

  if (!thumbData) {
    return null;
  }

  return (
    <PreviewThumb
      {...props}
      selected={selected}
      className={c(s['root'], isRemoved && s['removed'], selected && s['selected'], className)}
    >
      {deviation && (
        <IconButton
          className={s['btn-remove']}
          onClick={onRemove}
          size="small"
          icon={CloseIcon}
          aria-label={t('common.remove')}
          biData={
            deviation &&
            BiData<SubmitDeleteMultiImageFileClickBiEvent>({
              evid: 35,
              typeid: getDeviationResourceType(deviation),
              itemid: deviation.deviationId,
              sectionname: 'attachment_slider',
              rank: deviationAttachmentIds.indexOf(attachment.fileId!),
            })
          }
        />
      )}
      {OverlayIcon && (
        <OverlayIcon size={IconSize.MEDIUM} disableDefaultColors className={s['overlay-icon']} />
      )}
      <img
        src={thumbData.src}
        alt={attachment.filename}
        width={56}
        height={56}
        style={{ objectFit: 'cover' }}
      />
    </PreviewThumb>
  );
};
ImageAttachmentThumb.displayName = 'ImageAttachmentThumb';

export default ImageAttachmentThumb;
